import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminService } from '@/services/admin.service';
import { useToast } from '@/contexts/ToastContext';
import { ImageUpload } from '@/components';
import type { AdminTag, BadgeCriteria } from '@/types/admin';

const CATEGORIES = ['LEARNING', 'ENGAGEMENT', 'SOCIAL', 'MASTERY', 'SPECIAL'];
const RARITIES = ['COMMON', 'RARE', 'EPIC', 'LEGENDARY'];
const CRITERIA_TYPES = [
  { value: 'trails_completed', label: 'Cursos concluídos' },
  { value: 'xp_earned', label: 'XP total' },
  { value: 'trails_completed_with_tag', label: 'Cursos por tag' },
  { value: 'level_reached', label: 'Nível atingido' },
  { value: 'lessons_completed', label: 'Lições concluídas' },
] as const;

function describeCriteria(criteria: BadgeCriteria, tags: AdminTag[]): string {
  switch (criteria.type) {
    case 'trails_completed':
      return `Completar ${criteria.count || 0} cursos`;
    case 'xp_earned':
      return `Acumular >= ${criteria.xp || 0} XP`;
    case 'trails_completed_with_tag': {
      const tag = tags.find((t) => t.id === criteria.tag_id);
      return `Completar ${criteria.count || 0} cursos com tag "${tag?.name || '?'}"`;
    }
    case 'level_reached':
      return `Atingir nível ${criteria.level || 0}`;
    case 'lessons_completed':
      return `Completar ${criteria.count || 0} lições`;
    default:
      return criteria.type;
  }
}

const AdminBadgeFormPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const isEditing = !!id;

  const [form, setForm] = useState({
    title: '',
    description: '',
    icon: 'military_tech',
    icon_url: '',
    category: 'LEARNING',
    rarity: 'COMMON',
    xp_reward: 100,
    is_active: true,
  });

  const [criteria, setCriteria] = useState<BadgeCriteria>({ type: 'trails_completed', count: 5 });
  const [tags, setTags] = useState<AdminTag[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadTags();
    if (isEditing) loadBadge();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTags = async () => {
    try {
      const data = await adminService.getTags();
      setTags(data);
    } catch { /* ignore */ }
  };

  const loadBadge = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const badge = await adminService.getBadge(id);
      setForm({
        title: badge.title,
        description: badge.description,
        icon: badge.icon,
        icon_url: badge.icon_url,
        category: badge.category,
        rarity: badge.rarity,
        xp_reward: badge.xp_reward,
        is_active: badge.is_active,
      });
      setCriteria(badge.criteria);
    } catch {
      showToast('Erro ao carregar badge', 'error');
      navigate('/admin/badges');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCriteriaTypeChange = (type: BadgeCriteria['type']) => {
    switch (type) {
      case 'trails_completed':
      case 'lessons_completed':
        setCriteria({ type, count: 5 });
        break;
      case 'xp_earned':
        setCriteria({ type, xp: 1000 });
        break;
      case 'trails_completed_with_tag':
        setCriteria({ type, tag_id: tags[0]?.id || '', count: 3 });
        break;
      case 'level_reached':
        setCriteria({ type, level: 10 });
        break;
      default:
        setCriteria({ type, count: 5 });
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      showToast('Título é obrigatório', 'warning');
      return;
    }
    setIsSaving(true);
    try {
      if (isEditing && id) {
        await adminService.updateBadge(id, { ...form, criteria });
        navigate('/admin/badges');
      } else {
        const result = await adminService.createBadge({ ...form, criteria });
        if (result.retroactive_unlocks > 0) {
          showToast(`Badge criada! ${result.retroactive_unlocks} usuário(s) receberam a badge retroativamente.`, 'success');
        }
        navigate('/admin/badges');
      }
    } catch {
      showToast('Erro ao salvar badge', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="p-10 text-content-muted">Carregando...</div>;
  }

  return (
    <div className="p-6 lg:p-10 max-w-3xl">
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate('/admin/badges')} className="text-content-muted hover:text-content-primary">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h1 className="text-2xl font-bold">{isEditing ? 'Editar Badge' : 'Nova Badge'}</h1>
      </div>

      <div className="space-y-6">
        {/* Title + Description */}
        <div>
          <label className="block text-sm font-medium mb-1">Título *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
            placeholder="Nome da badge"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Descrição</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={3}
            className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none resize-none"
            placeholder="Descrição da badge"
          />
        </div>

        {/* Icon + Badge Image */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Ícone (Material Symbol)</label>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                maxLength={100}
                className="flex-1 px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
                placeholder="Ex: military_tech, emoji_events, star"
              />
              {form.icon && !form.icon_url && (
                <span className="material-symbols-outlined text-2xl text-accent">
                  {form.icon}
                </span>
              )}
            </div>
            <p className="text-xs text-content-muted mt-1">
              Fallback quando não há imagem.
              <a href="https://fonts.google.com/icons" target="_blank" rel="noopener noreferrer"
                 className="text-accent hover:underline ml-1">
                Ver ícones →
              </a>
            </p>
          </div>
          <ImageUpload
            value={form.icon_url}
            onChange={(url) => setForm({ ...form, icon_url: url })}
            folder="badges"
            label="Imagem da Badge"
            hint="PNG com fundo transparente, 256x256px recomendado"
          />
        </div>

        {/* Category + Rarity + XP */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Categoria</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Raridade</label>
            <select
              value={form.rarity}
              onChange={(e) => setForm({ ...form, rarity: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
            >
              {RARITIES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">XP Reward</label>
            <input
              type="number"
              value={form.xp_reward}
              onChange={(e) => setForm({ ...form, xp_reward: Number(e.target.value) })}
              onFocus={(e) => e.target.select()}
              className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
              min={0}
            />
          </div>
        </div>

        {/* Criteria Builder */}
        <div className="bg-surface-card border border-theme-border rounded-xl p-5 space-y-4" data-testid="criteria-builder">
          <h3 className="font-medium">Critério de Desbloqueio</h3>

          <div>
            <label className="block text-sm font-medium mb-1">Tipo</label>
            <select
              value={criteria.type}
              onChange={(e) => handleCriteriaTypeChange(e.target.value as BadgeCriteria['type'])}
              className="w-full px-4 py-2.5 rounded-xl bg-surface-primary border border-theme-border focus:border-accent focus:outline-none"
            >
              {CRITERIA_TYPES.map((ct) => (
                <option key={ct.value} value={ct.value}>{ct.label}</option>
              ))}
            </select>
          </div>

          {/* Dynamic fields */}
          {(criteria.type === 'trails_completed' || criteria.type === 'lessons_completed') && (
            <div>
              <label className="block text-sm font-medium mb-1">Quantidade</label>
              <input
                type="number"
                value={criteria.count || 0}
                onChange={(e) => setCriteria({ ...criteria, count: Number(e.target.value) })}
                onFocus={(e) => e.target.select()}
                className="w-full px-4 py-2.5 rounded-xl bg-surface-primary border border-theme-border focus:border-accent focus:outline-none"
                min={1}
              />
            </div>
          )}

          {criteria.type === 'xp_earned' && (
            <div>
              <label className="block text-sm font-medium mb-1">XP necessário ({'>='})</label>
              <input
                type="number"
                value={criteria.xp || 0}
                onChange={(e) => setCriteria({ ...criteria, xp: Number(e.target.value) })}
                onFocus={(e) => e.target.select()}
                className="w-full px-4 py-2.5 rounded-xl bg-surface-primary border border-theme-border focus:border-accent focus:outline-none"
                min={1}
              />
            </div>
          )}

          {criteria.type === 'level_reached' && (
            <div>
              <label className="block text-sm font-medium mb-1">Nível</label>
              <input
                type="number"
                value={criteria.level || 0}
                onChange={(e) => setCriteria({ ...criteria, level: Number(e.target.value) })}
                onFocus={(e) => e.target.select()}
                className="w-full px-4 py-2.5 rounded-xl bg-surface-primary border border-theme-border focus:border-accent focus:outline-none"
                min={1}
              />
            </div>
          )}

          {criteria.type === 'trails_completed_with_tag' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Tag</label>
                <select
                  value={criteria.tag_id || ''}
                  onChange={(e) => setCriteria({ ...criteria, tag_id: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl bg-surface-primary border border-theme-border focus:border-accent focus:outline-none"
                >
                  <option value="">Selecione uma tag</option>
                  {tags.map((tag) => (
                    <option key={tag.id} value={tag.id}>{tag.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Quantidade</label>
                <input
                  type="number"
                  value={criteria.count || 0}
                  onChange={(e) => setCriteria({ ...criteria, count: Number(e.target.value) })}
                  onFocus={(e) => e.target.select()}
                  className="w-full px-4 py-2.5 rounded-xl bg-surface-primary border border-theme-border focus:border-accent focus:outline-none"
                  min={1}
                />
              </div>
            </div>
          )}

          {/* Preview */}
          <div className="mt-3 p-3 rounded-lg bg-surface-primary border border-theme-border/50 text-sm text-content-secondary">
            <span className="font-medium">Preview: </span>
            {describeCriteria(criteria, tags)}
          </div>
        </div>

        {/* Save */}
        <div className="flex gap-3 pt-4">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-accent hover:bg-accent-hover text-accent-contrast px-6 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50"
          >
            {isSaving ? 'Salvando...' : isEditing ? 'Salvar Alterações' : 'Criar Badge'}
          </button>
          <button
            onClick={() => navigate('/admin/badges')}
            className="px-6 py-2.5 rounded-xl font-medium border border-theme-border text-content-muted hover:text-content-primary transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminBadgeFormPage;
